/**
 * Renders a list of trip sessions.
 *
 * This component is responsible for the trip navigation portion of the UI. It is driven by the global `TripsState`.
 * It reactively displays the list of recent trips, handles the creation of new trips by calling the `onNewTrip` callback,
 * and updates the global state (`TripsState.current`) when a user selects a different trip session.
 * Like other UI components in this section, it displays loading and empty states based on the status of the reactive query.
 */

import type { ClientInstanceOf } from '@elara/client-core/firebase/types';
import type { TripModel } from '@elara/client-core/models/TripModel';

import React from 'react';

import { ROUTES } from '@elara/client-core/constants';
import { generatePageTitle } from '@elara/client-core/lib/page-title';
import { cn } from '@elara/client-core/lib/utils';

import app from '#app.json' with { type: 'json' };
import { usePageTitle, useProductTourState } from '#states/hooks.js';
import { Button, EmptyState, Loader } from '#ui/index.js';
import { ImageCard } from '#views/ImageCard.js';
import { LinkButton } from '#views/Router.js';

interface TripItemProps {
  trip: ClientInstanceOf<TripModel>;
  size?: 'sm' | 'lg';
  onNavigate?: () => void;
}

/**
 * Renders a single trip item card via the shared {@link ImageCard} (the web counterpart of the mobile
 * `ImageCard`). A 16:9 cover with the title + dates overlaid bottom-left; past trips (`size="sm"`) are
 * desaturated and recolour on hover. Links to the trip overview.
 */
function TripItem({ trip, size = 'lg', onNavigate }: TripItemProps) {
  const title = trip.displayName || trip.title || app.defaults.chat.title;

  return (
    <ImageCard
      title={title}
      secondaryText={trip.cardSubtitle}
      image={trip.coverImage}
      orientation={size === 'sm' ? 'square' : 'landscape'}
      size={size === 'lg' ? 'large' : 'small'}
      desaturated={size === 'sm'}
      badge={trip.isDemo ? 'Demo' : undefined}
      to={ROUTES.TRIP}
      params={{ id: trip.id }}
      onClick={onNavigate}
    />
  );
}

/**
 * Displays a list of trips in a responsive grid layout.
 *
 * @param trips - Array of trip instances to display
 * @param size - Visual size variant: 'lg' for full cards with images, 'sm' for compact horizontal layout
 */
interface TripListProps {
  trips: Array<ClientInstanceOf<TripModel>>;
  size?: 'sm' | 'lg';
  /**
   * Whether the traveler has past trips further down the page. The empty state then greets them as a
   * returning member instead of welcoming a first-timer (ELARA-1030), and stays compact so the past
   * trips aren't pushed a full viewport below the fold.
   */
  hasPastTrips?: boolean;
}

/**
 * Renders the sidebar for trip navigation.
 */
function TripListComponent({ trips, size = 'lg', hasPastTrips = false }: TripListProps) {
  const productTourState = useProductTourState();
  const pageTitle = usePageTitle();

  // Past trips (`sm`) are small square tiles in a denser grid; current trips (`lg`) are wide cards.
  const gridClassName =
    size === 'sm'
      ? 'grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 w-full gap-6'
      : 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 w-full gap-6';

  return (
    <div className={cn(gridClassName, trips.length === 0 && 'h-full')}>
      {trips.length === 0 ? (
        <EmptyState
          title={hasPastTrips ? 'Welcome back' : 'Welcome to Elara'}
          description="Where will your travels take you next?"
          className={cn('col-span-full', hasPastTrips ? 'py-8 md:py-12' : 'min-h-[90dvh]')}
          illustration={<Loader thinking={true} width={80} height={80} />}
        >
          <div className="flex flex-col md:flex-row items-center gap-3">
            <LinkButton to={ROUTES.TRIP_NEW} size="lg">
              Plan a trip
            </LinkButton>
            <Button
              variant="outline"
              size="lg"
              className="bg-transparent"
              onClick={() => {
                setTimeout(async () => productTourState.start(), 50);
              }}
            >
              Explore a demo trip
            </Button>
          </div>
        </EmptyState>
      ) : (
        // Render the list of trip cards.
        trips.map((trip: ClientInstanceOf<TripModel>) => (
          <TripItem
            key={trip.id}
            trip={trip}
            size={size}
            onNavigate={() => {
              // Set page title when navigating to trip
              pageTitle.setTitle(generatePageTitle(trip.displayName));
            }}
          />
        ))
      )}
    </div>
  );
}

/**
 * Memoized TripList component to prevent unnecessary re-renders.
 * The component will only re-render when its props change.
 */
export const TripList = React.memo(TripListComponent);
